import React from 'react'

const featuresLeft = [
  {
    title: 'HTML, CSS & JavaScript',
    desc: 'Master the core building blocks of the modern web DOM.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
  },
  {
    title: 'Modern React & State',
    desc: 'Build dynamic, component-driven UI architectures.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" /></svg>
  },
  {
    title: 'APIs, Auth & Data',
    desc: 'Secure node backends and seamless structured JSON streaming.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>
  }
]

const featuresRight = [
  {
    title: 'Responsive Design',
    desc: 'Pixel-perfect, mobile-first TailwindCSS integrations.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
  },
  {
    title: 'Performance & SEO',
    desc: 'Optimize Core Web Vitals and accessible screen-reader logic.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
  },
  {
    title: 'Deployment & CI/CD',
    desc: 'Automated Vercel/Netlify global edge ecosystem hosting.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  }
]

const progressMetrics = [
  { label: 'Frontend UI Mastery', color: 'from-[#00cfff] to-[#0b3b8f]', width: 'w-[95%]' },
  { label: 'Backend API Integration', color: 'from-[#10b981] to-[#047857]', width: 'w-[88%]' },
  { label: 'Production React Patterns', color: 'from-[#f59e0b] to-[#b45309]', width: 'w-[92%]' }
]

export default function WebRoadmap() {
  return (
    <section id="web-roadmap" className="relative py-28 md:py-36 bg-slate-50 font-inter overflow-hidden border-t border-slate-200 min-h-[500px] lg:min-h-[650px] flex items-center">
      
      {/* Background Ambient Radiance */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#00cfff]/5 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#0b3b8f]/5 rounded-full blur-[120px] pointer-events-none"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <div className="inline-flex items-center gap-2 bg-[#00cfff]/10 border border-[#00cfff]/20 px-5 py-2 rounded-full mb-6">
            <span className="w-2.5 h-2.5 rounded-full bg-[#0b3b8f] animate-pulse"></span>
            <span className="text-xs font-extrabold tracking-widest text-[#0b3b8f] uppercase">Full-Stack Engineering</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
            Web Development <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0b3b8f] to-[#00cfff]">Roadmap</span>
          </h2>
          <p className="text-lg text-slate-600 font-medium leading-relaxed">
            Transition from fundamental logic constructs to architecting production-grade React applications with a heavily structured path covering UI styling, deep backend APIs, and global cloud deployments.
          </p>
        </div>

        {/* Feature & Image Grid */}
        <div className="grid lg:grid-cols-[1fr_1.5fr_1fr] gap-8 md:gap-12 items-center mb-24">
          
          {/* Left Cards */}
          <div className="order-2 lg:order-1 flex flex-col gap-6">
            {featuresLeft.map((f, i) => (
              <div key={i} className="group bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_2px_15px_rgba(0,0,0,0.03)] hover:shadow-xl hover:border-[#0b3b8f]/20 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
                <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-[#0b3b8f]/[0.02] to-transparent rounded-bl-full pointer-events-none transition-transform group-hover:scale-150"></div>
                <div className="w-12 h-12 rounded-2xl bg-[#0b3b8f]/5 text-[#0b3b8f] flex items-center justify-center mb-4 group-hover:bg-[#0b3b8f] group-hover:text-white transition-colors duration-300">
                  {f.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-[#0b3b8f] transition-colors">{f.title}</h3>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Central Holographic Image Frame */}
          <div className="order-1 lg:order-2 relative mx-auto w-full max-w-lg lg:max-w-none perspective-1000">
            <div className="relative rounded-3xl bg-slate-900 p-2 shadow-2xl shadow-[#00cfff]/10 hover:-translate-y-2 transition-transform duration-700">
              <div className="absolute inset-0 bg-gradient-to-br from-[#00cfff] via-[#0b3b8f] to-[#4b00cc] rounded-3xl opacity-20 blur-xl animate-pulse"></div>
              <img 
                src="/web development.webp" 
                alt="Web Development Journey Roadmap" 
                width="512"
                height="384"
                className="relative w-full h-auto rounded-[22px] border border-slate-700 object-cover z-10"
              />
              
              {/* Overlay Glass Badge */}
              <div className="absolute -top-6 -left-6 lg:-left-10 bg-white/95 backdrop-blur-md border border-slate-200 px-6 py-4 rounded-2xl shadow-xl z-20 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">Stack</div>
                  <div className="text-xl font-black text-slate-800">React + Node</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Cards */}
          <div className="order-3 flex flex-col gap-6">
            {featuresRight.map((f, i) => (
              <div key={i} className="group bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_2px_15px_rgba(0,0,0,0.03)] hover:shadow-xl hover:border-[#0b3b8f]/20 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
                <div className="absolute left-0 top-0 w-24 h-24 bg-gradient-to-bl from-[#0b3b8f]/[0.02] to-transparent rounded-br-full pointer-events-none transition-transform group-hover:scale-150"></div>
                <div className="w-12 h-12 rounded-2xl bg-[#0b3b8f]/5 text-[#0b3b8f] flex items-center justify-center mb-4 group-hover:bg-[#00cfff] group-hover:text-white transition-colors duration-300">
                  {f.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-[#0b3b8f] transition-colors">{f.title}</h3>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          
        </div>

        {/* Live Code Telemetry Grid */}
        <div className="max-w-4xl mx-auto bg-white rounded-3xl p-8 md:p-12 border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute right-0 bottom-0 w-64 h-64 bg-gradient-to-tl from-[#0b3b8f]/5 to-transparent rounded-full blur-[60px]"></div>
          
          <h3 className="text-xl font-extrabold text-slate-800 mb-8 flex items-center gap-3">
            <svg className="w-6 h-6 text-[#0b3b8f]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            Curriculum Penetration Metrics
          </h3>

          <div className="grid gap-6">
            {progressMetrics.map((b) => (
              <div key={b.label} className="relative z-10">
                <div className="flex justify-between text-sm font-bold text-slate-600 mb-2">
                  <span>{b.label}</span>
                  <span className="text-slate-600">{b.width.match(/\[(.*?)\]/)[1]}</span>
                </div>
                <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden shadow-inner">
                  <div className={`h-full bg-gradient-to-r ${b.color} ${b.width} rounded-full animate-pulse shadow-md transition-all duration-1000`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  )
}
